export type MapsApp = 'auto' | 'apple' | 'google' | 'waze';

/** Minimal structural shape accepted as a directions destination. */
export interface DirectionsDestination {
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

interface ResolvedDestination {
  /** Raw (un-encoded) destination string. */
  value: string;
  kind: 'address' | 'coords';
}

/**
 * Resolve a destination to a raw string, address-first with a coords fallback.
 * Returns null when there's nothing precise enough to navigate to
 * (city/state alone is too coarse — see spec).
 */
export function formatDestination(dest: DirectionsDestination): ResolvedDestination | null {
  const street = dest.address?.trim();
  if (street) {
    const parts = [street, dest.city, dest.state].filter(Boolean) as string[];
    let value = parts.join(', ');
    if (dest.zip) value = value ? `${value} ${dest.zip}` : dest.zip;
    return { value, kind: 'address' };
  }
  if (dest.latitude != null && dest.longitude != null) {
    return { value: `${dest.latitude},${dest.longitude}`, kind: 'coords' };
  }
  return null;
}

/** True when the destination has something we can navigate to. */
export function hasDirectionsDestination(dest: DirectionsDestination): boolean {
  return formatDestination(dest) !== null;
}

/** Pick the native maps provider for the current device. Never returns 'waze' (explicit-only). */
export function detectPlatform(userAgent: string = navigator.userAgent): 'apple' | 'google' {
  if (/iPhone|iPad|iPod|Macintosh|Mac OS X/i.test(userAgent)) return 'apple';
  if (/Android/i.test(userAgent)) return 'google';
  return 'google';
}

/** Validate a stored/raw value into a MapsApp, defaulting to 'auto'. */
export function parseMapsApp(value: string | null | undefined): MapsApp {
  return value === 'apple' || value === 'google' || value === 'waze' || value === 'auto'
    ? value
    : 'auto';
}

/**
 * Build a maps deep-link to `dest` for the chosen app.
 * Carries only the destination, so navigation starts from the device's current location.
 * Returns null when there's no usable destination.
 */
export function buildDirectionsUrl(
  dest: DirectionsDestination,
  app: MapsApp,
  userAgent?: string,
): string | null {
  const resolved = formatDestination(dest);
  if (!resolved) return null;

  const effectiveApp = app === 'auto' ? detectPlatform(userAgent) : app;
  const enc = encodeURIComponent(resolved.value);

  switch (effectiveApp) {
    case 'apple':
      return `https://maps.apple.com/?daddr=${enc}&dirflg=d`;
    case 'waze':
      return resolved.kind === 'coords'
        ? `https://waze.com/ul?ll=${enc}&navigate=yes`
        : `https://waze.com/ul?q=${enc}&navigate=yes`;
    case 'google':
    default:
      return `https://www.google.com/maps/dir/?api=1&destination=${enc}`;
  }
}
